/**
 * Whether a simulation failed, read from what the backend stores in `simulation_response`
 * (spec 76-06, contract C1). Mirrors the backend's `constants/simulation_status.py`.
 */

export const SIMULATION_GENERIC_ERROR = 'Sorry, something went wrong';
export const SIMULATION_INPUT_TOO_LONG_ERROR =
  'The input text exceeds the maximum length allowed by the Bluepill. Please reduce the amount of text or select fewer personas.';
export const INPUT_TOO_LONG_CODE = 'input_too_long';

const FAILED_STATUS = 'failed';
const LEGACY_SENTINELS = [SIMULATION_GENERIC_ERROR, SIMULATION_INPUT_TOO_LONG_ERROR];
const STRUCTURED_FAILURE_KEY = '"failure"';

export interface SimulationFailure {
  message: string;
  code: string | null;
  failedAt: string | null;
}

export interface SimulationDetailLike {
  status?: unknown;
  failure_message?: unknown;
  simulation_response?: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value ? value : null;

function failureFromMessage(
  message: string,
  code: string | null = null,
  failedAt: string | null = null,
): SimulationFailure {
  const knownCode =
    code ?? (message === SIMULATION_INPUT_TOO_LONG_ERROR ? INPUT_TOO_LONG_CODE : null);
  return { message, code: knownCode, failedAt };
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** The stored failure in `simulation_response`, or null for a running or completed run. */
export function simulationFailure(response: unknown): SimulationFailure | null {
  if (typeof response !== 'string') return null;
  const text = response.trim();
  if (!text) return null;
  if (LEGACY_SENTINELS.includes(text)) return failureFromMessage(text);

  const looksStructured = text.startsWith('{') && text.includes(STRUCTURED_FAILURE_KEY);
  if (!text.startsWith('"') && !looksStructured) return null;

  const parsed = parseJson(text);
  if (typeof parsed === 'string') {
    return LEGACY_SENTINELS.includes(parsed) ? failureFromMessage(parsed) : null;
  }
  if (!isRecord(parsed) || !isRecord(parsed.failure) || typeof parsed.error !== 'string') {
    return null;
  }
  return failureFromMessage(
    parsed.error,
    asString(parsed.failure.code),
    asString(parsed.failure.failed_at),
  );
}

/** As `simulationFailure`, also trusting the detail endpoint's derived `status`. */
export function simulationDetailFailure(detail: SimulationDetailLike): SimulationFailure | null {
  const stored = simulationFailure(detail.simulation_response);
  if (stored || detail.status !== FAILED_STATUS) return stored;
  return failureFromMessage(asString(detail.failure_message) ?? SIMULATION_GENERIC_ERROR);
}
